// Permission & visibility helpers driven by the logged-in user.
// Mirrors the canonical shape in permissions.ts (see spec).
import { ROLES } from "../constants";

export function isMain(u) { return u?.role === ROLES.MAIN; }
export function isRestMgr(u) { return u?.role === ROLES.RESTAURANT; }
export function isEmployee(u) { return u?.role === ROLES.EMPLOYEE; }

export function canManageAllRestaurants(user) {
  return isMain(user);
}

export function canManageRestaurant(user, restaurant) {
  if (isMain(user)) return true;
  if (isRestMgr(user) && user.restaurant === restaurant) return true;
  return false;
}

export function canEditProduct(user, product) {
  if (!user || !product) return false;
  if (isMain(user)) return true;
  if (isRestMgr(user)) {
    return (
      product.ownerRestaurant === user.restaurant ||
      (product.ownerRestaurant === "global" &&
        (product.assignedRestaurants || []).includes(user.restaurant))
    );
  }
  return false;
}

// Helper: normalise user's areas array (supports legacy `area` field)
export function userAreas(user) {
  if (!user) return [];
  if (Array.isArray(user.areas)) return user.areas;
  if (user.area) return [user.area];
  return [];
}

// Products the user is allowed to see. `requestType` narrows further:
// - purchase         → only availableForPurchase
// - internal_transfer→ only availableForTransfer
// - undefined        → just active + scope
export function getVisibleProducts(user, products, requestType) {
  return (products || []).filter((p) => {
    if (p.active === false) return false;
    if (requestType === "purchase" && p.availableForPurchase === false) return false;
    if (requestType === "internal_transfer" && p.availableForTransfer === false) return false;

    if (isMain(user)) return true;

    const inRestaurant =
      (p.assignedRestaurants || []).includes(user.restaurant) ||
      p.ownerRestaurant === user.restaurant;
    if (!inRestaurant) return false;

    if (isRestMgr(user)) return true;
    if (isEmployee(user)) {
      const uAreas = userAreas(user);
      if (uAreas.length === 0) return false;
      const pAreas = p.assignedAreas || [];
      // overlap: at least one area matches
      return uAreas.some((a) => pAreas.includes(a));
    }
    return false;
  });
}

// Legacy alias kept for existing callers
export function visibleProducts(user, products) {
  return getVisibleProducts(user, products);
}

export function getVisibleRequests(user, requests) {
  if (!user) return [];
  if (isMain(user)) return requests;
  if (isRestMgr(user)) {
    return requests.filter(
      (r) =>
        r.restaurant === user.restaurant ||
        r.fromRestaurant === user.restaurant ||
        r.toRestaurant === user.restaurant
    );
  }
  // Employees see:
  //   • their own requests (purchases + transfers they created)
  //   • ALL internal transfers in the system (so staff across both
  //     restaurants can coordinate what's moving where)
  return requests.filter(
    (r) => r.requestedBy === user.id || r.type === "internal_transfer"
  );
}

// Legacy alias
export function visibleRequests(user, requests) { return getVisibleRequests(user, requests); }

export function filterRequests(requests, filters = {}) {
  return requests.filter((r) => {
    if (filters.restaurant && filters.restaurant !== "all") {
      if (r.restaurant !== filters.restaurant && r.fromRestaurant !== filters.restaurant && r.toRestaurant !== filters.restaurant) return false;
    }
    if (filters.type && filters.type !== "all") {
      if (r.type !== filters.type) return false;
    }
    if (filters.status && filters.status !== "all") {
      if (r.status !== filters.status) return false;
    }
    if (filters.vendor && filters.vendor !== "all") {
      if (r.vendor !== filters.vendor) return false;
    }
    if (filters.requestedByName?.trim()) {
      const q = filters.requestedByName.trim().toLowerCase();
      if (!r.requestedByName?.toLowerCase().includes(q)) return false;
    }
    if (filters.productName?.trim()) {
      const q = filters.productName.trim().toLowerCase();
      const hit = (r.items || []).some((it) => (it.productName || "").toLowerCase().includes(q));
      if (!hit) return false;
    }
    if (filters.productId && filters.productId !== "all") {
      if (!(r.items || []).some((it) => it.productId === filters.productId)) return false;
    }
    if (filters.dateFrom) {
      if (new Date(r.createdAt).getTime() < new Date(filters.dateFrom).getTime()) return false;
    }
    if (filters.dateTo) {
      const end = new Date(`${filters.dateTo}T23:59:59`).getTime();
      if (new Date(r.createdAt).getTime() > end) return false;
    }
    return true;
  });
}

export function groupProductsByCategory(products) {
  return (products || []).reduce((acc, p) => {
    acc[p.category] = acc[p.category] || [];
    acc[p.category].push(p);
    return acc;
  }, {});
}

export function canManageUsers(user) { return isMain(user); }
export function canManageCatalog(user) { return isMain(user) || isRestMgr(user); }
// Any authenticated user can propose a transfer — only managers/admin
// can approve, mark fulfilled, or delete them once submitted.
export function canCreateTransfer(user) { return !!user; }
export function canApprove(user, request) {
  if (!user || !request) return false;
  if (isMain(user)) return true;
  if (isRestMgr(user)) {
    if (request.type === "purchase") return request.restaurant === user.restaurant;
    if (request.type === "internal_transfer") {
      return request.fromRestaurant === user.restaurant || request.toRestaurant === user.restaurant;
    }
  }
  return false;
}

// Who can confirm that a vendor actually delivered. Main manager can
// always; restaurant manager can confirm deliveries for their own
// restaurant (both for incoming purchases and incoming transfers).
export function canConfirmDelivery(user, request) {
  if (!user || !request) return false;
  if (isMain(user)) return true;
  if (isRestMgr(user)) {
    if (request.type === "purchase") return request.restaurant === user.restaurant;
    if (request.type === "internal_transfer") return request.toRestaurant === user.restaurant;
  }
  return false;
}

// Shortfall per line for a single request: ordered minus received.
export function lineShortfall(line) {
  const ordered = Number(line?.quantity ?? line?.qty ?? 0);
  const received = Number(line?.receivedQty ?? 0);
  return Math.max(0, ordered - received);
}

// Derive the outstanding shortfall list for the user's visible requests.
// Each entry represents a product that was ordered but not fully received
// and has not yet been converted into a direct purchase.
export function pendingDirectPurchases(requests, productsById) {
  const out = [];
  (requests || []).forEach((r) => {
    if (r.type !== "purchase") return;
    if (!r.deliveryConfirmedAt) return;
    (r.items || []).forEach((line) => {
      if (line.convertedToRequestId) return;
      const missing = lineShortfall(line);
      if (missing <= 0) return;
      const product = productsById ? productsById.get(line.productId) : null;
      out.push({
        key: `${r.id}::${line.productId}`,
        request: r,
        line,
        product,
        missing,
      });
    });
  });
  return out;
}

